import { PageActionBar } from "components/PageActionBar";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

/**
 * Unsaved benefit engine configuration, lifted from the card to the page.
 *
 * WHY THIS EXISTS. Most edits on the benefit configuration card happen in modals whose button only
 * stages the change on the card; nothing is written until "Save benefit configuration" at the
 * bottom of the card. People closed a modal, re-ran a check, and read the OLD configuration's
 * answer without knowing their edit was never saved. The card already knows when it differs from
 * what is saved (`dirty`), so it publishes that here and the page pins a bar to the viewport.
 *
 * 🚨 ENGINE CONFIGURATION ONLY. The rest of the account form has its own unsaved state and its own
 * save ("Update Hospital"). One bar driving two different saves was decided against on 2026-09-30.
 *
 * The bar lives at the PAGE level because `position: sticky` only pins inside its containing
 * block — a bar inside the card would scroll away with the card, which is the problem it solves.
 */
export interface IUnsavedBenefitConfig {
  /** False while the configuration is invalid; `blockedReason` says why. */
  canSave: boolean;
  blockedReason: string | null;
  saving: boolean;
  /** Resolves true when the configuration was written. */
  save: () => Promise<boolean>;
  discard: () => void;
}

type Setter = Dispatch<SetStateAction<IUnsavedBenefitConfig | null>>;

const StateContext = createContext<IUnsavedBenefitConfig | null>(null);
const SetterContext = createContext<Setter | null>(null);

export function UnsavedBenefitConfigProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [state, setState] = useState<IUnsavedBenefitConfig | null>(null);
  return (
    <SetterContext.Provider value={setState}>
      <StateContext.Provider value={state}>{children}</StateContext.Provider>
    </SetterContext.Provider>
  );
}

/** The card's unsaved changes, or null when there are none (or no card on the page). */
export function useUnsavedBenefitConfig() {
  return useContext(StateContext);
}

/**
 * Called by the card on every render. Publishes only when something the page shows changes, and
 * always calls the card's LATEST save/discard, so the page never saves a stale form.
 *
 * Outside a provider (the create-account page, tests) it does nothing.
 */
export function usePublishUnsavedBenefitConfig({
  dirty,
  canSave,
  blockedReason,
  saving,
  save,
  discard,
}: {
  dirty: boolean;
  canSave: boolean;
  blockedReason: string | null;
  saving: boolean;
  save: () => Promise<boolean>;
  discard: () => void;
}) {
  const setState = useContext(SetterContext);
  const saveRef = useRef(save);
  const discardRef = useRef(discard);
  saveRef.current = save;
  discardRef.current = discard;

  useEffect(() => {
    if (!setState) return;
    setState(
      dirty
        ? {
            canSave,
            blockedReason,
            saving,
            save: () => saveRef.current(),
            discard: () => discardRef.current(),
          }
        : null
    );
  }, [setState, dirty, canSave, blockedReason, saving]);

  // The card can unmount (the Eligibility section collapses, the page changes); its edits go with it.
  useEffect(
    () => () => {
      setState?.(null);
    },
    [setState]
  );
}

const Message = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  .unsaved-dot {
    width: 0.625rem;
    height: 0.625rem;
    border-radius: 50%;
    background: var(--orange-500, #f59e0b);
    flex-shrink: 0;
  }
`;

/**
 * The pinned bar. Shown from the first edit that differs from what is saved until the save lands
 * and the reloaded configuration matches the screen — so it comes back on the next edit, and
 * stays up if the save fails. While it is up, reloading or closing the tab asks first.
 */
export function UnsavedBenefitConfigBar() {
  const unsaved = useUnsavedBenefitConfig();
  const dirty = Boolean(unsaved);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Chrome still needs returnValue set; the text itself is never shown.
      // eslint-disable-next-line no-param-reassign
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const detail = useMemo(() => {
    if (!unsaved) return null;
    if (!unsaved.canSave && unsaved.blockedReason) return unsaved.blockedReason;
    return "Checks and estimates keep using the saved configuration until you save.";
  }, [unsaved]);

  if (!unsaved) return null;

  const confirmDiscard = () =>
    confirmDialog({
      header: "Discard unsaved changes?",
      message:
        "The benefit engine configuration goes back to what is saved. Your unsaved edits are lost.",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Discard changes",
      rejectLabel: "Keep editing",
      acceptClassName: "p-button-danger",
      accept: unsaved.discard,
    });

  return (
    <PageActionBar data-testid="benefitSelection.unsavedBar">
      <Message role="status">
        <span className="unsaved-dot" aria-hidden />
        <div className="flex flex-column">
          <span className="font-semibold">
            Unsaved benefit engine configuration changes
          </span>
          <span
            className="footer-hint text-sm"
            data-testid="benefitSelection.unsavedBar.detail"
          >
            {detail}
          </span>
        </div>
      </Message>
      <div className="flex gap-2">
        <Button
          type="button"
          label="Discard"
          className="p-button-text p-button-sm"
          disabled={unsaved.saving}
          data-testid="benefitSelection.unsavedBar.discard"
          onClick={confirmDiscard}
        />
        <Button
          type="button"
          label="Save benefit configuration"
          className="p-button-sm"
          loading={unsaved.saving}
          disabled={!unsaved.canSave}
          data-testid="benefitSelection.unsavedBar.save"
          onClick={() => {
            unsaved.save();
          }}
        />
      </div>
    </PageActionBar>
  );
}
